import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';

/**
 * Le BRANDING de l'environnement : le nom, le logo, l'icone et la couleur
 * d'accent que le client choisit lui-meme, tels que le shell les lit.
 *
 * DEUX SOURCES, UNE SEULE VERITE A L'ECRAN :
 *   1. le `taki.config.json` de deploiement (`branding.name`, `branding.logoUrl`,
 *      `branding.iconUrl`, `branding.colors.primary`), ecrit par Taki au
 *      deploiement : c'est le
 *      REPLI, celui qui s'affiche tant que le client n'a rien choisi ;
 *   2. le document Firestore `orgs/{orgId}/settings/branding`, ecrit par un
 *      owner ou un admin depuis Parametres > Environnement : c'est la
 *      PERSONNALISATION, champ par champ. Un champ absent du document retombe
 *      sur le repli.
 * `courant` est la fusion des deux, deja calculee par l'hote : le shell ne
 * connait que lui pour AFFICHER, et `personnalise` seulement pour EDITER.
 *
 * POURQUOI UN PORT, comme l'acces (`lib/acces.tsx`) : `@taki/ui` et le shell
 * ne parlent jamais au SDK. L'hote lit le document par `sdk.branding`, fusionne
 * avec la config et monte `BrandingProvider` ; sans provider, le shell affiche
 * la config, exactement comme avant ce chantier.
 *
 * POURQUOI NE PAS MODIFIER `config.branding` : `TakiShell` reconstruit son
 * routeur quand l'identite de `config` change. La personnalisation passe donc
 * par ce contexte, jamais par une copie modifiee de la config.
 */

/** Le lime Taki, la couleur d'accent par defaut du socle (`tokens.css`). */
export const ACCENT_TAKI = '#cbfb45';

/** Un hex a six chiffres, minuscules : la seule forme acceptee par les rules et par `deriverAccent`. */
export const MOTIF_HEX = /^#[0-9a-f]{6}$/;

/**
 * Ce que le client a choisi, champ par champ. Un champ absent ou `null` veut
 * dire "pas personnalise, garder le repli de la config".
 */
export interface BrandingPersonnalise {
  /** Le nom de l'environnement, affiche au login, a l'accueil et dans l'onglet. */
  name?: string | null;
  /** Le logo, une data URL (`data:image/...;base64,...` ou `data:image/svg+xml,...`). */
  logo?: string | null;
  /** L'icone carree (dock, onglet du navigateur), une data URL de meme forme que le logo. */
  icon?: string | null;
  /** La couleur d'accent, hex six chiffres minuscules. */
  accent?: string | null;
}

/** Le branding tel qu'il s'affiche : la fusion de la personnalisation et du repli. */
export interface BrandingEffectif {
  name: string;
  /** L'URL ou la data URL du logo, `null` si l'environnement n'a pas de logo (le nom s'affiche en texte). */
  logo: string | null;
  /** L'URL ou la data URL de l'icone, `null` si l'environnement n'en a pas (le chevron Taki tient la place). */
  icon: string | null;
  /** La couleur d'accent, hex six chiffres minuscules. */
  accent: string;
}

/** Ce que l'ecran Environnement enregistre. Un champ a `null` EFFACE la personnalisation de ce champ. */
export type BrandingPatch = BrandingPersonnalise;

export interface BrandingPort {
  /** Ce qui s'affiche maintenant. */
  courant: BrandingEffectif;
  /** Ce que le client a choisi (pour savoir ce qui est personnalise et ce qui ne l'est pas). */
  personnalise: BrandingPersonnalise;
  /** Le repli : ce que la config de deploiement dit. */
  repli: BrandingEffectif;
  /** `false` tant que le document Firestore n'a pas ete lu une premiere fois. */
  pret: boolean;
  /** Vrai pour un owner ou un admin actif : le meme droit que la gestion des membres. */
  peutModifier: boolean;
  /** Enregistre la personnalisation. Rejette avec l'erreur du SDK (`permission-denied` incluse). */
  enregistrer: (patch: BrandingPatch) => Promise<void>;
  /** Efface toute la personnalisation : l'environnement revient a sa config de deploiement. */
  reinitialiser: () => Promise<void>;
  /**
   * Previsualise sans enregistrer : l'ecran Environnement pousse son brouillon
   * ici pour que TOUTE l'interface se repeigne pendant l'edition. `null`
   * retire l'apercu.
   */
  previsualiser: (apercu: BrandingPatch | null) => void;
}

/** Normalise un hex saisi par l'utilisateur (`#ABC`, `ABCDEF`, espaces) en `#abcdef`, ou `null` s'il n'en est pas un. */
export function normaliserHex(entree: string | null | undefined): string | null {
  if (entree === null || entree === undefined) return null;
  let valeur = entree.trim().toLowerCase();
  if (valeur === '') return null;
  if (!valeur.startsWith('#')) valeur = `#${valeur}`;
  const court = valeur.match(/^#([0-9a-f]{3})$/);
  if (court?.[1] !== undefined) {
    const [r, g, b] = court[1].split('');
    valeur = `#${r}${r}${g}${g}${b}${b}`;
  }
  return MOTIF_HEX.test(valeur) ? valeur : null;
}

/**
 * La fusion, champ par champ : la personnalisation prime quand elle existe,
 * le repli sinon. Un accent qui n'est pas un hex valide est ignore (la config
 * accepte `rgb()` et `hsl()`, la derivation d'accent non : on retombe sur le
 * lime, exactement comme `tokens.css`).
 */
export function fusionnerBranding(
  repli: BrandingEffectif,
  personnalise: BrandingPersonnalise | null | undefined,
): BrandingEffectif {
  const p = personnalise ?? {};
  const name = typeof p.name === 'string' && p.name.trim() !== '' ? p.name.trim() : repli.name;
  const logo = typeof p.logo === 'string' && p.logo !== '' ? p.logo : repli.logo;
  /* `?? null` et pas `repli.icon` tout court : un repli fabrique a la main (un
     test, un hote qui n'a pas encore d'icone) peut n'avoir aucune clef `icon`,
     et `undefined` n'est pas `null` pour qui compare deux brandings. */
  const icon = typeof p.icon === 'string' && p.icon !== '' ? p.icon : (repli.icon ?? null);
  const accent = normaliserHex(p.accent) ?? repli.accent;
  return { name, logo, icon, accent };
}

/**
 * Le repli lu depuis la config de deploiement. `colors.primary` peut etre un
 * `rgb()` ou un `hsl()` (contrat de `parseBranding`) : dans ce cas la famille
 * d'accent ne se derive pas et on garde le lime des tokens.
 */
export function repliDepuisConfig(branding: {
  name: string;
  logoUrl?: string;
  iconUrl?: string;
  colors: { primary: string };
}): BrandingEffectif {
  return {
    name: branding.name,
    logo: branding.logoUrl ?? null,
    icon: branding.iconUrl ?? null,
    accent: normaliserHex(branding.colors.primary) ?? ACCENT_TAKI,
  };
}

const BrandingContext = createContext<BrandingPort | null>(null);

export function BrandingProvider({ value, children }: { value: BrandingPort; children: ReactNode }) {
  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}

/** Le port de branding, ou `null` sans provider (le shell lit alors la config). */
export function useBrandingPort(): BrandingPort | null {
  return useContext(BrandingContext);
}
